import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import MHeader from "@/components/ui/organisms/MHeader";
import { getMemberInfo } from "@/hooks/useMembers";
import { ListItem, ListItemAction } from "@/components/ui/atoms/MListItem";
import { useReminders } from "@/hooks/useReminders";
import {
  BaseReminders,
  Reminders,
  ReminderSubType,
  ReminderType,
} from "@/types/reminders";
import {
  IconSearch,
  IconEdit,
  IconCaretDownFilled,
  IconInfoSquare,
  IconCircleX,
  IconPillFilled,
  IconBellX,
} from "@tabler/icons-react";
import styles from "./reminder.module.css";
import {
  IconVaccination,
  IconDoctor,
  IconLabTest,
  IconTherapySession,
  IconInsurance,
  IconHealthCheckup,
  IconPaw,
  IconFrequency,
} from "@/components/icons";
import MDialog from "@/components/ui/atoms/MDialog";
import { useToast } from "@/components/ui/atoms/MToastProvider";
import MDropDownDrawer from "@/components/ui/molecules/MDropDownDrawer";
import { DerivedMemberType } from "@/types/member";
import MLoader from "@/components/ui/atoms/MLoader";
import { useNavigate } from "react-router-dom";
import { useVaccinations } from "@/hooks/useVaccinations";
import usePetCare from "@/hooks/usePetCare";
import MEmptyCard from "@/components/ui/organisms/MEmptyCard";
import MSpacer from "@/components/ui/atoms/MSpacer";
import MInfiniteScroll from "@/components/ui/atoms/MInfiniteScroll";
import {
  getSecondaryText,
  getEmphasizedText,
  getTertiaryText,
  getTertiaryIcon,
} from "./remindersHelper";
import { MTourTrigger } from "@/components/ui/organisms/MTourTrigger";
import { MHelpIcon } from "@/components/ui/atoms/MHelpIcon";
import { useNavigationSource } from "@/hooks/useNavigationSource";
import { trackUserDataLoaded } from "@/utils/gtm";
import {
  ANALYTICS_SCREEN_TYPES,
  ANALYTICS_SCREEN_NAMES,
} from "@/constants/analytics.constants";

/**
 * RemindersListing component
 * Displays a list of reminders with search, filter, and CRUD operations
 */
interface RemindersListingProps {
  onAddReminder: () => void;
}

const RemindersListing: React.FC<RemindersListingProps> = ({
  onAddReminder,
}) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const tourTriggerRef = React.useRef<any>(null);
  const memberInfo = getMemberInfo();
  const { navigateBack, hasNavigationSource } = useNavigationSource();
  const memberId = memberInfo.id || "";
  // Get member name as requested for personalized messages
  const {
    reminders,
    loading,
    fetchReminders,
    fetchMoreReminders,
    deleteReminder,
    category,
    setCategory,
    searchTerm,
    setSearchTerm,
    hasMore,
    isLoadingMore,
  } = useReminders(memberId);
  const [inputValue, setInputValue] = useState(searchTerm);
  const [selectedCategory, setSelectedCategory] = useState<ReminderType>(
    ReminderType.ALL
  );
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedReminderId, setSelectedReminderId] = useState<string | null>(
    null
  );
  const [showEditDrawer, setShowEditDrawer] = useState(false);
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const { getVaccination } = useVaccinations(memberId);
  const { getPetCare } = usePetCare(memberId);
  const [petCareDataMap, setPetCareDataMap] = useState<
    Record<string, { frequencyType: string; frequency?: number }>
  >({});

  // Fetch reminders when component mounts or member changes
  // Single useEffect to handle both memberId and category changes
  useEffect(() => {
    if (memberId) {
      (async () => {
        setInitialLoadDone(false);
        await fetchReminders();
        setInitialLoadDone(true);
      })();
    }
  }, [memberId, category, fetchReminders]);

  useEffect(() => {
    const fetchPetCareData = async () => {
      const petCareReminders = reminders.filter(
        (r) => r.type === ReminderType.PET_CARE
      );
      const newPetCareDataMap: Record<
        string,
        { frequencyType: string; frequency?: number }
      > = {};

      for (const reminder of petCareReminders) {
        if (reminder.sourceId && !petCareDataMap[reminder.sourceId]) {
          try {
            const petCare = await getPetCare(reminder.sourceId);
            if (petCare) {
              newPetCareDataMap[reminder.sourceId] = {
                frequencyType: petCare.frequencyType,
                frequency: petCare.frequency,
              };
            }
          } catch (error) {
            console.error("Error fetching pet care data:", error);
          }
        }
      }

      if (Object.keys(newPetCareDataMap).length > 0) {
        setPetCareDataMap((prev) => ({ ...prev, ...newPetCareDataMap }));
      }
    };

    if (reminders.length > 0) {
      fetchPetCareData();
    }
  }, [reminders, getPetCare]);

  // Handle search when user clicks search icon
  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  // Handle category selection
  const handleCategorySelect = (categorySelected: ReminderType) => {
    setSelectedCategory(categorySelected);
    setShowFilterDrawer(false);
    setCategory(categorySelected);
    // No need to call fetchReminders() here as the useEffect will handle it
  };

  // Generate actions for each reminder item
  const getReminderActions = (reminder: BaseReminders): ListItemAction[] => {
    return [
      {
        id: "edit",
        label: t("common.edit"),
        icon: <IconEdit size={20} />,
        onClick: () => handleEditReminder(reminder),
        isVisible: true,
      },
      {
        id: "delete",
        label: t("common.delete"),
        icon: <IconCircleX size={20} className="text-semantic-icon-error" />,
        onClick: () => showDeleteReminderDialog(reminder.id),
        isVisible: true,
        className: "text-semantic-icon-error",
      },
    ];
  };

  const getFilterItems = (): { value: string; label: string }[] => {
    const reminderItems = [
      ReminderType.ALL,
      ReminderType.MEDICATION,
      ReminderType.APPOINTMENT,
      ReminderType.INSURANCE,
      ReminderType.VACCINATION,
    ];
    switch (memberInfo?.derivedMemberType) {
      case DerivedMemberType.P:
        reminderItems.push(ReminderType.PET_CARE);
        break;
    }
    const finalRenderItems = reminderItems.map((item) => ({
      value: item,
      label: t(`reminders.filterTypes.${item}`),
    }));
    return finalRenderItems;
  };

  const getIcon = (reminder: BaseReminders) => {
    switch (reminder.type) {
      case ReminderType.MEDICATION:
        return <IconPillFilled />;
      case ReminderType.APPOINTMENT:
        switch (reminder.subType) {
          case ReminderSubType.DOCTOR_VISIT:
            return <IconDoctor />;
          case ReminderSubType.LAB_TEST:
            return <IconLabTest />;
          case ReminderSubType.THERAPY:
            return <IconTherapySession />;
          case ReminderSubType.HEALTH_CHECKUP:
            return <IconHealthCheckup />;
          default:
            return <IconInfoSquare />;
        }
      case ReminderType.INSURANCE:
        return <IconInsurance />;
      case ReminderType.VACCINATION:
        return <IconVaccination />;
      case ReminderType.PET_CARE:
        return <IconPaw />;
      default:
        return <IconInfoSquare />;
    }
  };

  // Handle edit reminder
  const handleEditReminder = async (reminder: BaseReminders) => {
    // Navigate to edit page or open edit modal
    console.log("Edit reminder:", reminder);
    setSelectedReminderId(reminder.id);
    switch (reminder.type) {
      case ReminderType.MEDICATION:
        navigate(`/reminders/medication`, { state: { reminder } });
        break;
      case ReminderType.APPOINTMENT:
        navigate(`/reminders/appointment`, { state: { reminder } });
        break;
      case ReminderType.INSURANCE:
        navigate(`/reminders/insurance`, { state: { reminder } });
        break;
      case ReminderType.VACCINATION:
        try {
          const vaccination = await getVaccination(reminder.sourceId);
          navigate(`/reminders/add-vaccine`, { state: { vaccination } });
        } catch (error) {
          showToast({
            message: t("reminders.errors.errorFetchingVaccination"),
            variant: "error",
          });
        }
        break;
      case ReminderType.PET_CARE:
        navigate("/reminders/pet-care");
        break;
      default:
        break;
    }
  };

  const handleEditReminderDone = () => {
    setShowEditDrawer(false);
    setSelectedReminderId(null);
    fetchReminders();
  };

  const showDeleteReminderDialog = (reminderId: string) => {
    setSelectedReminderId(reminderId);
    setShowDeleteDialog(true);

    // Track delete click
    trackUserDataLoaded({
      memberInfo,
      screenType: ANALYTICS_SCREEN_TYPES.REMINDER,
      screenName: ANALYTICS_SCREEN_NAMES.REMINDER_HOME,
      action: "delete_click",
    });
  };

  // Handle delete reminder
  const handleDeleteReminder = async () => {
    // show delete confirmation dialog
    await deleteReminder(selectedReminderId);
    setSelectedReminderId(null);
    setShowDeleteDialog(false);

    // Track delete confirmation
    trackUserDataLoaded({
      memberInfo,
      screenType: ANALYTICS_SCREEN_TYPES.REMINDER,
      screenName: ANALYTICS_SCREEN_NAMES.REMINDER_HOME,
      action: "delete_confirm",
    });

    fetchReminders();
  };

  if (loading) {
    return <MLoader />;
  }

  return (
    <>
      <div className={styles.headerHelpIcon} data-wizard-help-icon>
        <MHelpIcon onClick={() => tourTriggerRef.current?.open()} />
      </div>
      {/* Header */}
      <MHeader
        module="reminders_listing"
        memberInfo={memberInfo}
        headerLabel="Reminders"
        showProfilePickerRow
        onAdd={onAddReminder}
        // showSearch={selectedCategory !== ReminderType.ALL}
        searchPlaceholder={t("reminders.listing.searchPlaceholder")}
        value={inputValue}
        onValueChange={setInputValue}
        onSearch={handleSearch}
        onClear={() => {
          setInputValue("");
          setSearchTerm("");
        }}
        showBackButton={hasNavigationSource()}
        onBack={navigateBack}
        showGradient={true}
        description={
          "Set reminders for yourself and others. All reminders you create are sent only to your phone."
        }
        learnMoreContent={
          <>
            When you set a reminder—no matter who it is for—it will appear only
            in your profile, and the alert will be sent only to your phone.
            <br /> <br /> For example, if you set a reminder for your father's
            blood test, the notification will come to your device at the date
            and time you've set.
          </>
        }
      />

      {loading ? (
        <MLoader />
      ) : (
        <>
          {initialLoadDone && reminders.length === 0 && searchTerm && (
            <>
              <MSpacer mt={24} />
              <MEmptyCard
                primaryTitle={t("reminders.searchEmpty")}
                secondaryTitle={t("reminders.searchEmptyDescription")}
                icon={<IconSearch />}
              />
            </>
          )}

          {initialLoadDone &&
            reminders.length === 0 &&
            !searchTerm &&
            selectedCategory === ReminderType.ALL && (
              <>
                <MSpacer mt={24} />
                <MEmptyCard
                  primaryTitle={t("reminders.listing.noRemindersSet")}
                  secondaryTitle={t("reminders.listing.noRemindersHelp")}
                  icon={<IconBellX />}
                  primaryCTA={{
                    text: t("reminders.listing.addReminder"),
                    onClick: onAddReminder,
                  }}
                />
              </>
            )}

          {/* Reminders list */}
          <div>
            <MSpacer mt={24} />
            <div className={styles.listingContainer}>
              {(reminders.length > 0 ||
                selectedCategory !== ReminderType.ALL) && (
                <button
                  type="button"
                  className={styles.filterDropdown}
                  onClick={() => setShowFilterDrawer(true)}
                  aria-label={t("reminders.filterTitle")}
                >
                  <span>{t(`reminders.filterTypes.${selectedCategory}`)}</span>
                  <IconCaretDownFilled size={16} />
                </button>
              )}

              {initialLoadDone &&
                reminders.length === 0 &&
                !searchTerm &&
                selectedCategory !== ReminderType.ALL && (
                  <div className="w-full">
                    <MEmptyCard
                      primaryTitle={t("reminders.categoryEmpty")}
                      icon={<IconBellX />}
                    />
                  </div>
                )}

              {reminders.length > 0 && (
                <MInfiniteScroll
                  data={reminders}
                  hasMore={hasMore}
                  isLoading={isLoadingMore}
                  onLoadMore={fetchMoreReminders}
                  className="w-full"
                  renderItem={(reminder) => {
                    const isTherapy =
                      reminder.type === ReminderType.APPOINTMENT &&
                      reminder.subType === ReminderSubType.THERAPY;
                    const isAppointmentNotTherapy =
                      reminder.type === ReminderType.APPOINTMENT && !isTherapy;
                    const petCareData =
                      reminder.type === ReminderType.PET_CARE &&
                      reminder.sourceId
                        ? petCareDataMap[reminder.sourceId]
                        : undefined;

                    return (
                      <ListItem
                        key={reminder.id}
                        title={reminder.reminderFor}
                        secondaryText={getSecondaryText(reminder)}
                        emphasizedText={getEmphasizedText(
                          reminder,
                          t,
                          petCareData
                        )}
                        emphasizedIcon={
                          reminder.type !== ReminderType.APPOINTMENT ||
                          isTherapy ? (
                            <IconFrequency size={12} color="#2A2A2A" />
                          ) : undefined
                        }
                        tertiaryText={getTertiaryText(reminder)}
                        tertiaryIcon={getTertiaryIcon(reminder)}
                        tertiaryVariant="orange"
                        icon={getIcon(reminder)}
                        iconPosition="offset"
                        actions={getReminderActions(reminder)}
                        usage="crud"
                        type="3-line"
                        variant="purple"
                        className={
                          isAppointmentNotTherapy
                            ? styles.appointmentReminder
                            : undefined
                        }
                      />
                    );
                  }}
                />
              )}
            </div>
          </div>

          {/* Delete reminder modal */}
          <MDialog
            isOpen={showDeleteDialog}
            onClose={() => setShowDeleteDialog(false)}
            title={t("reminders.deleteReminder.title")}
            description={t("reminders.deleteReminder.description")}
            primaryCtaText={t("common.yes")}
            onPrimaryCta={handleDeleteReminder}
            secondaryCtaText={t("common.no")}
            onSecondaryCta={() => setShowDeleteDialog(false)}
            showCloseButton={true}
          />

          {/* Filter drawer */}
          <MDropDownDrawer
            open={showFilterDrawer}
            onClose={() => setShowFilterDrawer(false)}
            title={t("reminders.filterTitle")}
            initialValue={selectedCategory}
            items={getFilterItems()}
            onSelect={handleCategorySelect}
            variant="single"
          />
        </>
      )}

      <MTourTrigger ref={tourTriggerRef} page="reminders" />
    </>
  );
};

export default RemindersListing;
